import React from 'react';
import { Box, Inline } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import analytics from '../../../modules/analytics';

import { useMount } from '../../../hooks';
import PortalOverlay, {
  PortalOverlayModal,
  PortalOverlayHeader,
  PortalOverlayHeaderCloseBtn,
} from '../../ui/PortalOverlay';
import {
  Absolute, BodyText, Button, HeaderText, Stack,
} from '../../ui';
import {
  BasicCircleCheckedIcon,
  BasicCircleXIcon,
  SecurityKeyIcon,
  TimeCalendarCheckedIcon,
  MapsRadarIcon,
  VariousSmartPricingIcon,
} from '../../ui/Icons';
import { getIsApp } from '../../../modules/AppInfo';

export type SmartPricingAnnouncementModalParams = {
  onClose: () => void;
  providerId?: number;
  source?: string;
};

export const BASE_ID = 'pro-smartpricing-education';
export const TRACKING_PREFIX = 'pro_smartpricing_education_';

const SmartPricingAnnouncementModal: React.FC<SmartPricingAnnouncementModalParams> = (
  {
    onClose,
    providerId,
    source,
  },
) => {
  const isApp = getIsApp();
  const linkTarget = isApp ? '_system' : '_blank';

  const handleCloseClick = (description: string) => {
    analytics.track(`${TRACKING_PREFIX}${description}_click`, { providerId });
    onClose();
  };

  useMount(() => {
    analytics.track(`${TRACKING_PREFIX}view`, { providerId, source });
  });

  return (
    <PortalOverlay name={BASE_ID} overflowScroll>
      <PortalOverlayModal position="fullScreen">
        <PortalOverlayHeader
          alignItems="center"
          renderRightAction={() => (
            <PortalOverlayHeaderCloseBtn
              testID={`${BASE_ID}-top-close-button`}
              onCloseRequest={() => handleCloseClick('close')}
              color={colors.silver}
              size={20}
            />
          )}
        />
        <Absolute zIndex={-1}>
          <Stack space={8} color={colors.whiteWash}>
            <Stack color={colors.lightPurple} space={16} padding={24} paddingBottom={36}>
              <Inline space={4}>
                <VariousSmartPricingIcon width={8} height={21} />
                <BodyText color={colors.purple}>Smart Pricing with StyleSeat</BodyText>
              </Inline>
              <HeaderText>How top pros on StyleSeat grew their business</HeaderText>
              <Inline space={12}>
                <Stack
                  space={8}
                  paddingX={12}
                  paddingY={24}
                  color={colors.whiteWash}
                  maxWidth="calc(50% - 6px)"
                  alignItems="center"
                  borderRadius={8}
                  showBoxShadow
                >
                  <BasicCircleCheckedIcon color={colors.purple} />
                  <BodyText textAlign="center" appearance="medium">
                    They made their chair more profitable on an hour by hour basis.
                  </BodyText>
                </Stack>
                <Stack
                  space={8}
                  paddingX={12}
                  paddingY={24}
                  color={colors.whiteWash}
                  maxWidth="calc(50% - 6px)"
                  alignItems="center"
                  borderRadius={8}
                  showBoxShadow
                >
                  <BasicCircleXIcon color={colors.red} />
                  <BodyText textAlign="center" appearance="medium">
                    They didn’t discount their services to attract deal hunters.
                  </BodyText>
                </Stack>
              </Inline>
            </Stack>
            <Stack paddingX={24} paddingTop={32} space={24}>
              <HeaderText level={2} textAlign="center">
                Here’s how you can make an extra $3K a year with Smart Pricing
              </HeaderText>
              <Inline space={16}>
                <MapsRadarIcon color={colors.purple} width={32} height={28} />
                <BodyText>
                  StyleSeat uses machine learning to find your high-demand time slots
                </BodyText>
              </Inline>
              <Inline space={16}>
                <TimeCalendarCheckedIcon color={colors.purple} width={32} height={28} />
                <BodyText>
                  We raise the prices by 20%
                  <BodyText color={colors.metal}> (for those times only) </BodyText>
                  to match their true value
                </BodyText>
              </Inline>
              <Inline space={16}>
                <SecurityKeyIcon color={colors.purple} width={32} height={28} />
                <BodyText>
                  We make sure your serious clients are happy and continue to book
                </BodyText>
              </Inline>
            </Stack>
            <Box padding={24}>
              <Stack
                padding={24}
                color={colors.lightPurple}
                borderRadius={8}
                space={16}
                showBoxShadow
              >
                <BodyText textAlign="center">
                  “I don’t mind paying more for the time I really want.
                  My stylist is busy and I value their time.”
                </BodyText>
                <BodyText textAlign="center" color={colors.metal}>
                  - Laura G.
                </BodyText>
              </Stack>
            </Box>
            <HeaderText level={3} textAlign="center">Have Questions?</HeaderText>
            <Stack space={8} alignItems="center" paddingBottom={24}>
              <Button
                testID={`${BASE_ID}-learn-more-button`}
                appearance="link"
                style={{
                  color: colors.metal,
                  borderBottom: `solid 2px ${colors.metal}`,
                }}
                linkTarget={linkTarget}
                href="https://www.styleseat.com/blog/earn-more-with-smart-pricing"
                clickTracking={{
                  name: `${TRACKING_PREFIX}learn_more_click`,
                  params: { providerId },
                }}
              >
                Learn more
              </Button>
              <Button
                testID={`${BASE_ID}-contact-us-button`}
                appearance="link"
                style={{
                  color: colors.metal,
                  borderBottom: `solid 2px ${colors.metal}`,
                }}
                linkTarget={linkTarget}
                href="https://styleseat.freshdesk.com/support/tickets/new"
                clickTracking={{
                  name: `${TRACKING_PREFIX}contact_us_click`,
                  params: { providerId },
                }}
              >
                Contact Us
              </Button>
            </Stack>
            <Box padding={24} borderTopColor={colors.lightGrey} borderTopWidth={1}>
              <Button
                testID={`${BASE_ID}-got-it-button`}
                onClick={() => handleCloseClick('got_it')}
              >
                Got it
              </Button>
            </Box>
          </Stack>
        </Absolute>
      </PortalOverlayModal>
    </PortalOverlay>
  );
};

export default SmartPricingAnnouncementModal;
